import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { getDoctorById } from './doctorSlice';
import { getPatientById } from './patientSlice';
import { getAdminById } from './adminSlice';
import i18n from '../i18n';
import { bindSession } from '../utils/sessionGuard';
import { cacheStaffAccess } from '../hooks/usePermissions';
import { cachePlatformAccess, clearPlatformAccess } from '../utils/platformPermissions';

// Async thunk for user login

// ---------------------------------------------------------------------------
// STORE A SESSION. Everything a successful sign-in writes to the browser —
// tokens, role flags, tenant hints, cached permissions, the tab binding, the
// language, the login events — in one place, so the password login and the
// second-factor verify (mfaVerify) store exactly the same thing.
// ---------------------------------------------------------------------------
function storeSession({ accessToken, refreshToken, user }) {
    // 🔹 Save tokens after successful fetch
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('ID', user.id);
    // Normalise super_admin to the admin UI: super_admin IS the full admin
    // experience (it just keeps Audit & Security + Subscription sub-items,
    // which a plain admin doesn't). Storing Role='admin' means every existing
    // role==='admin' check works unchanged; the only client-side difference
    // is the isSuperAdmin flag, read by the Sidebar + route guards. The real
    // role stays in the JWT, so the backend still enforces super_admin-only.
    //
    // PLATFORM STAFF keep their own role: they are the Super Admin's team,
    // and what they may open is decided by their role's permissions (below),
    // not by the isSuperAdmin flag — which stays 'false' for them.
    localStorage.setItem('Role', user.role === 'super_admin' ? 'admin' : user.role);
    localStorage.setItem('isSuperAdmin', user.role === 'super_admin' ? 'true' : 'false');
    // Which tenant (hospital/clinic) this session acts inside. Empty for
    // super_admin and patients, which span tenants by design. Display/UX
    // only — the backend reads the tenant from the signed JWT, so a tampered
    // value here grants nothing. Read it via hooks/useSession.js.
    localStorage.setItem('TenantId', user.tenantId || '');
    // The tenant's country — staff-creation forms show it read-only rather
    // than asking, since doctors/admins inherit it from their tenant.
    localStorage.setItem('TenantCountryId', user.tenantCountryId || '');
    // STAFF (role 'user'): the sub-role they hold and what it grants, so the
    // sidebar is right on the first paint instead of drawing an empty menu
    // and filling it in a moment later. Refreshed from /user-roles/me by
    // hooks/usePermissions.js, and never trusted — the API resolves every
    // permission from the database on each request.
    cacheStaffAccess(user.staff || null);
    // PLATFORM STAFF: same idea one level up — `{ permissionId: true }` +
    // role name, read by utils/platformPermissions.js for the sidebar and
    // the support-queue route guard. Presentation only; every support
    // event re-resolves the permission server-side.
    cachePlatformAccess(user.role === 'platform_staff' ? (user.platformStaff || null) : null);
    localStorage.setItem('isAuthenticated', 'true');

    // Bind this tab's session — see utils/sessionGuard.js. Must happen
    // before any other tab can read the new ACTIVE_SESSION_KEY, which is
    // why it sits in the same synchronous block as the token writes.
    bindSession();

    // 🌐 Apply the user's saved language preference from the DB. First-time
    // users default to 'en' (set by the schema). Returning users see the app
    // in the language they last picked, even on a new browser / incognito.
    if (user.lang) {
      try {
        localStorage.setItem('i18nextLng', user.lang);
        if (i18n.language !== user.lang) {
          i18n.changeLanguage(user.lang);
        }
      } catch (e) { /* noop */ }
    }

    // 🔔 Trigger event to initialize FCM and socket
    window.dispatchEvent(new Event('userLoggedIn'));
    // Also fire the `login` event so AppContent refetches the master locale
    // cache for the (possibly-new) language.
    try { window.dispatchEvent(new Event('login')); } catch (e) { /* noop */ }
}

export const userlogin = createAsyncThunk(
  'auth/userlogin',
  async ({ formdata, axiosInstance }, { rejectWithValue, dispatch }) => {
    try {
      // If this tab was previously exiled by the cross-tab session guard
      // (another tab logged in/out as a different user), lift the exile
      // *before* making the request so the axios pre-flight check doesn't
      // cancel our login attempt with a generic "Login failed" toast.
      try { sessionStorage.removeItem('tabExiled'); } catch { /* noop */ }

      // Get FCM token from localStorage
      const fcmToken = localStorage.getItem('fcmToken');

      // Add FCM token to login request
      const loginData = {
        ...formdata,
        fcmToken: fcmToken || null // Send token if available, null otherwise
      };

      const response = await axiosInstance.post('/auth/login', loginData);
      // SUPER ADMIN SECOND FACTOR (infra review, 26 Sep 2026): the password
      // was right but the session is not issued yet — the page shows the
      // authenticator step and finishes with mfaVerify below. A structured
      // rejection (no toast) so the page can branch on it.
      if (response.data?.mfaRequired || response.data?.mfaSetupRequired) {
        return rejectWithValue({ mfa: response.data.mfaSetupRequired ? 'setup' : 'verify', mfaToken: response.data.mfaToken });
      }
      const { accessToken, refreshToken, user } = response.data.data;

      if (accessToken && refreshToken && user) {
        storeSession({ accessToken, refreshToken, user });
        return { accessToken, refreshToken, user };
      }

      if (accessToken && refreshToken && user) {
        // 🔹 Ensure role-based APIs are awaited
        if (user.role === 'doctor') {
          await dispatch(getDoctorById(user.id)).unwrap();
        } else if (user.role === 'admin') {
          await dispatch(getAdminById(user.id)).unwrap();
        } else if (user.role === 'patient') {
          await dispatch(getPatientById(user.id)).unwrap();
        }
      };


      toast.warning('Invalid response format', { autoClose: 2000 });
      return rejectWithValue('Invalid response format');
    } catch (error) {
      const data = error.response?.data;
      // Bulk-uploaded patients are gated until they verify their email + set a
      // new password. Surface a structured payload (no toast) so the login page
      // can route them into the activation flow instead of showing an error.
      if (data?.verificationRequired) {
        return rejectWithValue({ verificationRequired: true, email: data.email, phone: data.phone || null });
      }
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const errorMessage =
        (lang === 'fr'
          ? data?.message?.fr
          : data?.message?.en) || 'Login failed';
      // Structured auth errors (TENANT_SELECTION_REQUIRED, the login-identifier
      // codes) are the login page's to present — pass them through instead of
      // toasting a dead-end string here. Without this the page's err.code
      // handlers never fire, since unwrap() would throw a bare string.
      if (data?.code) {
        return rejectWithValue({ code: data.code, tenants: data.tenants, message: errorMessage });
      }
      toast.warning(errorMessage, { autoClose: 2000 });
      return rejectWithValue(errorMessage);
    }
  }
);

// Send the email OTP that activates a bulk-uploaded patient account.

// ---------------------------------------------------------------------------
// SUPER ADMIN SECOND FACTOR. mfaSetup hands back the secret + otpauth URI to
// enrol an authenticator app; mfaVerify checks the code and, when it is right,
// receives exactly what /auth/login would have — the same session is stored
// the same way (the login thunk's fulfilled handler runs for both).
// ---------------------------------------------------------------------------
const mfaMessage = (data, fallback) => {
  const lang = localStorage.getItem('i18nextLng') || 'en';
  return (lang === 'fr' ? data?.message?.fr : data?.message?.en) || data?.message || fallback;
};

export const mfaSetup = createAsyncThunk(
  'auth/mfaSetup',
  async ({ mfaToken, axiosInstance }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/auth/mfa/setup', { mfaToken });
      return { secret: data.secret, otpauthUri: data.otpauthUri, account: data.account, issuer: data.issuer };
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue({ code: data?.code || null, message: mfaMessage(data, 'Could not start the authenticator setup') });
    }
  }
);

export const mfaVerify = createAsyncThunk(
  'auth/mfaVerify',
  async ({ mfaToken, code, axiosInstance }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/mfa/verify', { mfaToken, code });
      const { accessToken, refreshToken, user } = response.data.data;
      if (!(accessToken && refreshToken && user)) return rejectWithValue({ code: 'MFA_BAD_RESPONSE', message: 'Invalid response format' });
      storeSession({ accessToken, refreshToken, user });
      return { accessToken, refreshToken, user };
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue({ code: data?.code || null, message: mfaMessage(data, 'That code is not right') });
    }
  }
);

export const sendActivationOtp = createAsyncThunk(
  'auth/sendActivationOtp',
  async ({ email, axiosInstance }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/send-otp', { email });
      return response.data;
    } catch (error) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const msg = (lang === 'fr' ? error.response?.data?.message?.fr : error.response?.data?.message?.en)
        || error.response?.data?.message || error.message || 'Failed to send code';
      return rejectWithValue(msg);
    }
  }
);

// Verify the OTP + set a new password; on success the backend auto-logs the
// patient in (returns tokens), so we persist the session exactly like userlogin.
export const activateAccount = createAsyncThunk(
  'auth/activateAccount',
  // The OTP codes travel WITH the activation request and are consumed
  // server-side — there is no client-asserted "already verified" any more
  // (that flag let anyone claim any pending account). `identifier` is the
  // email or mobile number the account is known by; tenantId names WHICH
  // hospital's account when the identifier holds several.
  async ({ identifier, tenantId, emailOtp, phoneOtp, newPassword, axiosInstance }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/activate', {
        identifier,
        ...(tenantId ? { tenantId } : {}),
        ...(emailOtp ? { emailOtp } : {}),
        ...(phoneOtp ? { phoneOtp } : {}),
        newPassword,
      });
      const { accessToken, refreshToken, user } = response.data.data || {};
      if (accessToken && refreshToken && user) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('ID', user.id);
        // Normalise super_admin to the admin UI: super_admin IS the full admin
        // experience (it just keeps Audit & Security + Subscription sub-items,
        // which a plain admin doesn't). Storing Role='admin' means every existing
        // role==='admin' check works unchanged; the only client-side difference
        // is the isSuperAdmin flag, read by the Sidebar + route guards. The real
        // role stays in the JWT, so the backend still enforces super_admin-only.
        localStorage.setItem('Role', user.role === 'super_admin' ? 'admin' : user.role);
        localStorage.setItem('isSuperAdmin', user.role === 'super_admin' ? 'true' : 'false');
        // Which tenant (hospital/clinic) this session acts inside. Empty for
        // super_admin and patients, which span tenants by design. Display/UX
        // only — the backend reads the tenant from the signed JWT, so a tampered
        // value here grants nothing. Read it via hooks/useSession.js.
        localStorage.setItem('TenantId', user.tenantId || '');
        // The tenant's country — staff-creation forms show it read-only rather
        // than asking, since doctors/admins inherit it from their tenant.
        localStorage.setItem('TenantCountryId', user.tenantCountryId || '');
        // STAFF (role 'user'): the sub-role they hold and what it grants, so the
        // sidebar is right on the first paint instead of drawing an empty menu
        // and filling it in a moment later. Refreshed from /user-roles/me by
        // hooks/usePermissions.js, and never trusted — the API resolves every
        // permission from the database on each request.
        cacheStaffAccess(user.staff || null);
        // Same platform-staff permission cache as the login path above.
        cachePlatformAccess(user.role === 'platform_staff' ? (user.platformStaff || null) : null);
        localStorage.setItem('isAuthenticated', 'true');
        bindSession();
        if (user.lang) {
          try {
            localStorage.setItem('i18nextLng', user.lang);
            if (i18n.language !== user.lang) i18n.changeLanguage(user.lang);
          } catch { /* noop */ }
        }
        window.dispatchEvent(new Event('userLoggedIn'));
        try { window.dispatchEvent(new Event('login')); } catch { /* noop */ }
        return { accessToken, refreshToken, user };
      }
      return rejectWithValue('Invalid response format');
    } catch (error) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const msg = (lang === 'fr' ? error.response?.data?.message?.fr : error.response?.data?.message?.en)
        || error.response?.data?.message || error.message || 'Activation failed';
      return rejectWithValue(msg);
    }
  }
);



// Async thunk for token validation
export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async ({ axiosInstance }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/refresh');
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message?.en || 'Invalid token';
      toast.warning(errorMessage, { autoClose: 2000 });
      localStorage.clear();
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for logging out. Unregisters this browser's FCM token from the
// backend BEFORE clearing local storage, so subsequent pushes destined for
// another account can't land on this signed-out browser. Falls back to a pure
// client-side logout if the API call fails (e.g. offline or token expired).
export const performLogout = createAsyncThunk(
  'auth/performLogout',
  async ({ axiosInstance, lang } = {}, { dispatch }) => {
    const fcmToken = localStorage.getItem('fcmToken');
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      // Always hit the logout endpoint when authenticated (not just when an
      // FCM token exists), so the backend can blacklist the access + refresh
      // tokens and reject any stale tabs that try to reuse them.
      if (axiosInstance && (fcmToken || refreshToken)) {
        await axiosInstance.post('/auth/logout', { fcmToken, refreshToken });
      }
    } catch (err) {
      // Non-fatal: proceed with client-side cleanup so the user is signed out
      // locally even if the server call fails.
      console.warn('Logout API call failed; continuing client-side cleanup:', err?.message);
    } finally {
      dispatch(logout({ lang }));
    }
  }
);

// Async thunk for persisting the user's UI language preference to the backend.
// Called from the header popup when the user picks a new language, and from the
// login flow as a best-effort sync. The backend accepts the userId either from
// the JWT (via authenticateToken middleware) or from the body — we send both to
// stay compatible with either wiring and with role-specific fields the backend
// supports (doctorId / patientId).
export const updateUserLanguage = createAsyncThunk(
  'auth/updateUserLanguage',
  async ({ lang, axiosInstance }, { rejectWithValue }) => {
    try {
      const userId = localStorage.getItem('ID');
      const role = localStorage.getItem('Role');
      const body = { lang };
      if (userId) {
        body.userId = userId;
        // Also set the role-specific field for backward compatibility with the
        // original endpoint signature which expected doctorId / patientId.
        if (role === 'patient') body.patientId = userId;
        else body.doctorId = userId; // covers doctor + admin
      }
      const response = await axiosInstance.put('/auth/update-language', body);
      return response.data;
    } catch (error) {
      // Swallow — language persistence is non-critical; UI still switches locally.
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update language'
      );
    }
  }
);

// Async thunk for forgot password.
// tenantId is optional but matters when the same email holds accounts at
// several hospitals: it names WHICH account's password is being reset. Without
// it, the backend answers 409 TENANT_SELECTION_REQUIRED with the options.
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, tenantId, axiosInstance }, { rejectWithValue }) => {
    try {
      // `email` may hold an email OR a mobile number (per the platform's
      // login-identifier mode) — sent as `identifier`, which the backend
      // classifies. Responds with { id, maskedEmail }: the OTP always goes to
      // the account's registered inbox, and maskedEmail says which one
      // without disclosing the address to a phone-only caller.
      const response = await axiosInstance.post('/auth/forgot-password', {
        identifier: email,
        ...(tenantId ? { tenantId } : {}),
      });
      return response.data.data; // { id, maskedEmail }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for verifying OTP
export const verifyingOtp = createAsyncThunk(
  'auth/verifyingOtp',
  async ({ email, otpCode, tenantId, axiosInstance }, { rejectWithValue }) => {
    try {
      // Same identifier contract as forgot-password: the backend re-resolves a
      // mobile number to the account's email server-side (the OTP row is keyed
      // by email, and returning the address itself would leak phone → email).
      const response = await axiosInstance.post('/auth/reset-password', {
        identifier: email,
        otpCode,
        ...(tenantId ? { tenantId } : {}),
      });
      return response.data.data;
    } catch (error) {
      // const errorMessage = error.response?.data?.message?.en;
      // toast.warning(errorMessage, { autoClose: 2000 });
      return rejectWithValue(error);
    }
  }
);

// Async thunk for reset password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ docId, email, password, confirmPassword, resetToken, axiosInstance }, { rejectWithValue }) => {
    try {
      // resetToken: the single-use proof verifyingOtp's answer carries
      // (data.resetToken). The server refuses the reset without it (403).
      const response = await axiosInstance.put(`/auth/resetPassword/${docId}`, {
        email,
        password,
        confirmPassword,
        resetToken,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ✅ Check Email Existence
export const checkEmailExists = createAsyncThunk(
  'auth/checkEmailExists',
  async ({ email, tenantId, axiosInstance }, { rejectWithValue }) => {
    try {
      // tenantId scopes the answer to one hospital — an email taken at another
      // hospital is still free here, since accounts are per-tenant. Omitted =
      // the old platform-wide answer (over-blocks; kept for old callers).
      const response = await axiosInstance.post('/auth/emailExist', {
        email: email.trim(),
        ...(tenantId ? { tenantId } : {}),
      });
      // Response structure: { exists: true/false, message: { en: "...", fr: "..." } }
      return response.data;
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

// ✅ Check Mobile Number Existence
export const checkMobileExists = createAsyncThunk(
  'auth/checkMobileExists',
  async ({ phone, tenantId, axiosInstance }, { rejectWithValue }) => {
    try {
      // tenantId scopes the answer to one hospital, same as checkEmailExists.
      const response = await axiosInstance.post('/auth/mobileNoExist', {
        phone: phone.trim(),
        ...(tenantId ? { tenantId } : {}),
      });
      // Response structure: { exists: true/false, message: { en: "...", fr: "..." } }
      return response.data;
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

// ✅ Send OTP (email or phone)
export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async ({ email, phone, axiosInstance }, { rejectWithValue }) => {
    try {
      const payload = email ? { email } : { phone };
      const response = await axiosInstance.post('/auth/send-otp', payload);
      return response.data;
    } catch (error) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const errorMessage =
        (lang === 'fr'
          ? error.response?.data?.message?.fr
          : error.response?.data?.message?.en) || 'Failed to send OTP';
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Verify OTP (email or phone)
export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ email, phone, otpCode, axiosInstance }, { rejectWithValue }) => {
    try {
      const payload = email ? { email, otpCode } : { phone, otpCode };
      const response = await axiosInstance.post('/auth/verify-otp', payload);
      return response.data;
    } catch (error) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const errorMessage =
        (lang === 'fr'
          ? error.response?.data?.message?.fr
          : error.response?.data?.message?.en) || 'OTP verification failed';
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Update User Password
export const userUpdatePassword = createAsyncThunk(
  'auth/userUpdatePassword',
  async ({ docId, currentPassword, newPassword, confirmPassword, axiosInstance }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/admin/reset-password/${docId}`, {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      return response.data;
    } catch (error) {
      const lang = localStorage.getItem('i18nextLng') || 'en';
      const errorMessage =
        (lang === 'fr'
          ? error.response?.data?.message?.fr
          : error.response?.data?.message?.en) || 'Password update failed';
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    error: null,
    emailCheckLoading: false,
    emailCheckResult: null, // { exists: true/false, message: {...} }
    emailCheckError: null,
    mobileCheckLoading: false,
    mobileCheckResult: null, // { exists: true/false, message: {...} }
    mobileCheckError: null,
    sendOtpLoading: false,
    sendOtpError: null,
    verifyOtpLoading: false,
    verifyOtpError: null,
  },
  reducers: {
    logout: (state, action) => {
      const { lang } = action.payload;

      // Check language and set toast message accordingly
      const message =
        lang === 'fr'
          ? 'Déconnexion réussie !' // French
          : 'Logout successful!';   // English (default)

      toast.success(message, { autoClose: 2000 });

      // Clear user session first
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      // Explicit even though clear() below wipes it: the platform-staff
      // permission cache decides which pages render, so its removal at logout
      // must not depend on the blanket clear staying a blanket clear.
      clearPlatformAccess();
      localStorage.clear();
      // Drop this tab's session binding. localStorage.clear() already removed
      // the shared activeSessionId; without this, sessionStorage would still
      // hold the old per-tab id and a subsequent login in this same tab would
      // briefly look like a mismatch. Also clear the exile flag so a re-login
      // in this tab (after a force-logout earlier in the session) starts clean.
      try {
        sessionStorage.removeItem('tabSessionId');
        sessionStorage.removeItem('tabExiled');
      } catch { /* noop */ }

      // Dispatch logout event for other components (including socket cleanup)
      window.dispatchEvent(new Event('logout'));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userlogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userlogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        // Notify the app that auth state flipped to logged-in so the master-locale
        // cache can be refetched (logout clears localStorage, so cache is empty here).
        try { window.dispatchEvent(new Event('login')); } catch (e) { /* noop */ }
      })
      .addCase(userlogin.rejected, (state, action) => {
        state.isLoading = false;
        // A second-factor pause is not an error — the page continues with
        // mfaSetup / mfaVerify; nothing to show, nothing to flip.
        if (action.payload?.mfa) return;
        state.error = action.payload;
        localStorage.setItem('isAuthenticated', 'false');
      })
      // The second factor finishing a login IS the login: same state as
      // userlogin.fulfilled so the page's post-login effect takes over.
      .addCase(mfaVerify.pending, (state) => { state.isLoading = true; })
      .addCase(mfaVerify.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        try { window.dispatchEvent(new Event('login')); } catch (e) { /* noop */ }
      })
      .addCase(mfaVerify.rejected, (state) => { state.isLoading = false; })
      .addCase(activateAccount.fulfilled, (state, action) => {
        // Same shape as a successful login — the backend auto-logs the patient
        // in after activation, so flip the app to the logged-in state.
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        try { window.dispatchEvent(new Event('login')); } catch { /* noop */ }
      })
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.accessToken = localStorage.getItem('accessToken');
        state.refreshToken = localStorage.getItem('refreshToken');
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        localStorage.clear();
      })
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(verifyingOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyingOtp.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(verifyingOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Check Email Existence
      .addCase(checkEmailExists.pending, (state) => {
        state.emailCheckLoading = true;
        state.emailCheckError = null;
        state.emailCheckResult = null;
      })
      .addCase(checkEmailExists.fulfilled, (state, action) => {
        state.emailCheckLoading = false;
        state.emailCheckResult = action.payload;
        state.emailCheckError = null;
      })
      .addCase(checkEmailExists.rejected, (state, action) => {
        state.emailCheckLoading = false;
        state.emailCheckError = action.payload?.error || 'Failed to check email';
        state.emailCheckResult = null;
      })
      // Check Mobile Number Existence
      .addCase(checkMobileExists.pending, (state) => {
        state.mobileCheckLoading = true;
        state.mobileCheckError = null;
        state.mobileCheckResult = null;
      })
      .addCase(checkMobileExists.fulfilled, (state, action) => {
        state.mobileCheckLoading = false;
        state.mobileCheckResult = action.payload;
        state.mobileCheckError = null;
      })
      .addCase(checkMobileExists.rejected, (state, action) => {
        state.mobileCheckLoading = false;
        state.mobileCheckError = action.payload?.error || 'Failed to check mobile number';
        state.mobileCheckResult = null;
      })
      // Send OTP
      .addCase(sendOtp.pending, (state) => {
        state.sendOtpLoading = true;
        state.sendOtpError = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.sendOtpLoading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.sendOtpLoading = false;
        state.sendOtpError = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.verifyOtpLoading = true;
        state.verifyOtpError = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.verifyOtpLoading = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.verifyOtpLoading = false;
        state.verifyOtpError = action.payload;
      })
      // Update User Password
      .addCase(userUpdatePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userUpdatePassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(userUpdatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;